import { useMonarchSlot } from '@redventures/cohesion-utils-react';
import Cookies from 'js-cookie';
import { setCookie } from '~/helpers/cookie';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PAGE_ROUTES } from '~/constants/pages';
import { setNGCConfiguration } from '~/redux/actions/recommendation';
import { NGCConfiguration } from '~/types/recommendation';
import { ReduxState } from '~/types/redux';
import { cartConfigAddressCheckDefaultValue } from '../../monarch/Config | Address Check/default';
import { ngcFlowDefaultValue, NgcFlowSlotData } from '../../monarch/Config | NGC Flow/default';

export const BUY_FLOW = {
  NGC: 'NGC',
  BAU: 'BAU',
  DEFAULT: 'DEFAULT',
} as const;

export type BuyFlow = (typeof BUY_FLOW)[keyof typeof BUY_FLOW];

const BUY_FLOW_COOKIE_KEY = 'buyFlow';

const getBuyFlowFromQueryParam = (buyFlowParam?: string | string[]): BuyFlow | undefined => {
  if (buyFlowParam === BUY_FLOW.NGC || buyFlowParam === BUY_FLOW.BAU) {
    return buyFlowParam;
  }
  return undefined;
};

export const useBuyFlowDecision = ({ addressCheckCompleted }: { addressCheckCompleted: boolean }): void => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { address, products, canNavigateToBuyFlowRedirect, enrichedCustomer } = useSelector(
    (store: ReduxState) => store
  );
  const { techAvailable } = address || {};
  const isCopper = techAvailable === 'COPPER';
  const [buyFlow, setBuyFlow] = useState<BuyFlow | undefined>();
  const [persistedNGCConfiguration, setPersistedNGCConfiguration] = useState(false);

  // Read cookie value to make this hook reactive to cookie changes
  const buyFlowCookie = Cookies.get(BUY_FLOW_COOKIE_KEY) as BuyFlow | undefined;

  // Address check slot - determines buy flow (NGC/BAU/DEFAULT)
  const { isLoading: buyFlowLoading, data: buyFlowResult } = useMonarchSlot('addressCheck', {
    default: cartConfigAddressCheckDefaultValue,
  });

  // NGC Flow slot - provides NGC config values via traffic flow
  // This replaces the previous separate Monarch rule approach
  const { data: ngcFlowData } = useMonarchSlot<NgcFlowSlotData>('cartConfigNgcFlows', {
    default: ngcFlowDefaultValue,
  });

  const canRedirect =
    address?.install?.address &&
    products?.available &&
    canNavigateToBuyFlowRedirect &&
    enrichedCustomer &&
    !buyFlowLoading;

  // Effect that sets the buy flow (NGC or BAU)
  useEffect(() => {
    if (!addressCheckCompleted) return;

    const buyFlowQueryParam = getBuyFlowFromQueryParam(router.query?.buyFlow);

    // Priority order:
    // 1. Query parameter (highest priority)
    // 2. Cookie value (set by NgcVsBau or other components)
    // 3. Monarch buyFlowResult
    // 4. Default to BAU
    if (buyFlowQueryParam) {
      setBuyFlow(buyFlowQueryParam);
    } else if (buyFlowCookie && (buyFlowCookie === BUY_FLOW.NGC || buyFlowCookie === BUY_FLOW.BAU)) {
      setBuyFlow(buyFlowCookie);
    } else if (buyFlowResult && buyFlowResult.buyFlow) {
      if (
        buyFlowResult.buyFlow === BUY_FLOW.NGC ||
        buyFlowResult.buyFlow === BUY_FLOW.BAU ||
        buyFlowResult.buyFlow === BUY_FLOW.DEFAULT
      ) {
        setBuyFlow(buyFlowResult.buyFlow);
      }
    } else if (!buyFlowLoading) {
      setBuyFlow(BUY_FLOW.BAU);
    }
  }, [router.query?.buyFlow, buyFlowCookie, buyFlowResult, addressCheckCompleted, buyFlowLoading]);

  // Sets the NGC configuration from traffic flow slot. Only runs if buy flow is NGC
  useEffect(() => {
    if (!addressCheckCompleted || buyFlow !== BUY_FLOW.NGC) return;

    // Get NGC configuration from the traffic flow slot data
    // Map ngcFlow -> recommendationFlow for Redux state
    const slotData = ngcFlowData || ngcFlowDefaultValue;
    const ngcConfiguration: NGCConfiguration = {
      recommendationFlow: slotData.ngcFlow,
      model: slotData.model,
      showFaq: slotData.showFaq,
      showMoreDetailsByop: slotData.showMoreDetailsByop,
      altProductExplainerVersion: slotData.altProductExplainerVersion,
      v2AddOnCheckbox: slotData.v2AddOnCheckbox,
      claimRewardsLocation: slotData.claimRewardsLocation,
      enableStickyCtaOnQuiz: slotData.enableStickyCtaOnQuiz,
    };

    dispatch(setNGCConfiguration(ngcConfiguration));
    setPersistedNGCConfiguration(true);
  }, [ngcFlowData, dispatch, buyFlow, addressCheckCompleted]);

  useEffect(() => {
    if (!canRedirect || !buyFlow) return;
    if (buyFlow === BUY_FLOW.NGC && !persistedNGCConfiguration) return;
    if (buyFlow === BUY_FLOW.DEFAULT) {
      router.push(PAGE_ROUTES.BUY_FLOW_REDIRECT);
    } else if (buyFlow === BUY_FLOW.NGC && !isCopper) {
      setCookie(BUY_FLOW_COOKIE_KEY, BUY_FLOW.NGC, { otGroup: 'C0003' });
      router.push(PAGE_ROUTES.QUIZ);
      window.sessionStorage.setItem('hasEnteredNGCQuiz', 'true');
    } else {
      setCookie(BUY_FLOW_COOKIE_KEY, BUY_FLOW.BAU, { otGroup: 'C0003' });
      router.push(products?.isCopper2Fiber ? PAGE_ROUTES.PLAN_FIBER : PAGE_ROUTES.PLAN_PACKAGE);
    }
  }, [canRedirect, buyFlow, router, products?.isCopper2Fiber, persistedNGCConfiguration]);
};
